import { useState, useCallback, useEffect } from 'react';
import { PartnerLinkApi, WarehouseInvoiceApi, ManifestApi, CourierPackageApi } from '@shared/sdk-v1/apis';
import { PartnerLink, WarehouseInvoice, UnpaidInvoiceSummary, Manifest } from '@shared/sdk-v1/models';
import { apiConfig } from '../shared/api-config';
import { useAuth } from '../auth/hooks/useAuth';

export interface ManifestWithStats extends Manifest {
  userPackageCount: number;
  userTotalWeight: number;
}

interface UseWarehouseDashboardReturn {
  partnerLinks: PartnerLink[];
  recentInvoices: WarehouseInvoice[];
  unpaidSummary: UnpaidInvoiceSummary | null;
  totalPackages: number;
  totalManifests: number;
  recentManifests: ManifestWithStats[];
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

export const useWarehouseDashboard = (): UseWarehouseDashboardReturn => {
  const { user, courier } = useAuth();
  const [partnerLinks, setPartnerLinks] = useState<PartnerLink[]>([]);
  const [recentInvoices, setRecentInvoices] = useState<WarehouseInvoice[]>([]);
  const [unpaidSummary, setUnpaidSummary] = useState<UnpaidInvoiceSummary | null>(null);
  const [totalPackages, setTotalPackages] = useState(0);
  const [totalManifests, setTotalManifests] = useState(0);
  const [recentManifests, setRecentManifests] = useState<ManifestWithStats[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!user?.id || !courier?.id) return;

    setIsLoading(true);
    setError(null);

    const config = apiConfig();
    const partnerLinkApi = new PartnerLinkApi(config);
    const invoiceApi = new WarehouseInvoiceApi(config);
    const manifestApi = new ManifestApi(config);
    const packageApi = new CourierPackageApi(config);

    try {
      // Phase A: Fetch partner links first (needed for warehouseTenantId)
      let links: PartnerLink[] = [];
      try {
        links = await partnerLinkApi.partnerLinkGetPartnerLinks({
          tenantId: String(courier.id),
          role: 'sub-courier',
        });
        setPartnerLinks(links);
      } catch {
        setPartnerLinks([]);
      }

      const activeLink = links.find((l) => l.status === 'active');
      const warehouseCourierId = activeLink?.warehouseTenantId;

      // Phase B: Fetch all other data in parallel
      const [invoicesResult, summaryResult, packageCountResult, manifestsResult] = await Promise.allSettled([
        invoiceApi.warehouseInvoiceGetMyInvoices({
          subCourierId: user.id as number,
          filters: { limit: 10 },
        }),
        invoiceApi.warehouseInvoiceGetUnpaidInvoiceSummary({
          subCourierId: user.id as number,
        }),
        packageApi.courierPackageCount({
          where: JSON.stringify({ ownerId: user.id }),
        }),
        warehouseCourierId
          ? manifestApi.manifestFind({
              filter: JSON.stringify({
                where: { courierId: warehouseCourierId },
                order: 'startDate DESC',
                limit: 10,
                include: [{
                  relation: 'courierPackages',
                  scope: {
                    where: { ownerId: user.id },
                    fields: { id: true, weight: true, status: true, trackingNo: true },
                  },
                }],
              }),
            })
          : Promise.resolve([]),
      ]);

      if (invoicesResult.status === 'fulfilled') {
        setRecentInvoices(invoicesResult.value);
      }
      if (summaryResult.status === 'fulfilled') {
        setUnpaidSummary(summaryResult.value);
      }
      if (packageCountResult.status === 'fulfilled') {
        setTotalPackages((packageCountResult.value as any)?.count || 0);
      }
      if (manifestsResult.status === 'fulfilled') {
        const manifestsData = manifestsResult.value as Manifest[];
        // Filter to only manifests that have packages belonging to this user
        const manifestsWithStats: ManifestWithStats[] = manifestsData
          .map((m) => {
            const userPkgs = m.courierPackages || [];
            return {
              ...m,
              userPackageCount: userPkgs.length,
              userTotalWeight: userPkgs.reduce((sum, p: any) => sum + (p.weight || 0), 0),
            };
          })
          .filter((m) => m.userPackageCount > 0);
        setRecentManifests(manifestsWithStats);
        setTotalManifests(manifestsWithStats.length);
      }

      // Only set error if all requests failed
      const allFailed = [invoicesResult, summaryResult, packageCountResult, manifestsResult].every(
        (r) => r.status === 'rejected'
      );
      if (allFailed) {
        setError('Failed to load warehouse data. Please try again.');
      }
    } catch (err) {
      console.error('Error fetching warehouse dashboard data:', err);
      setError('Failed to load warehouse data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, courier?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refresh = useCallback(() => {
    fetchData();
  }, [fetchData]);

  return {
    partnerLinks,
    recentInvoices,
    unpaidSummary,
    totalPackages,
    totalManifests,
    recentManifests,
    isLoading,
    error,
    refresh,
  };
};
